import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Ban, Loader2, ShieldCheck, UserPlus } from "lucide-react";
import { useState } from "react";
import { GhostButton, Modal, PrimaryButton } from "#/components/admin/modal";
import { authClient } from "#/lib/auth-client";
import {
	homeRouteFor,
	ROLE_HINTS,
	ROLE_LABELS,
	ROLES,
	type Role,
} from "#/lib/roles";
import { getSession } from "#/lib/session";

export const Route = createFileRoute("/admin/users")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (session.user.role !== "admin") {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: UsersPage,
});

interface AdminUser {
	id: string;
	name: string;
	email: string;
	role?: string | null;
	banned?: boolean | null;
}

const USERS_QUERY_KEY = ["admin", "users"];

function UsersPage() {
	const queryClient = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const usersQuery = useQuery({
		queryKey: USERS_QUERY_KEY,
		queryFn: async () => {
			const { data, error: err } = await authClient.admin.listUsers({
				query: { limit: 200, sortBy: "name", sortDirection: "asc" },
			});
			if (err) throw new Error(err.message ?? "تعذر تحميل المستخدمين");
			return data.users as AdminUser[];
		},
	});

	function invalidate() {
		void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
	}

	const setRoleMutation = useMutation({
		// `as never`: adminClient() defaults to "admin"|"user", but our roles are commercial/admin and aren't checked against any better-auth `roles` config.
		mutationFn: (vars: { userId: string; role: string }) =>
			authClient.admin.setRole({
				userId: vars.userId,
				role: vars.role as never,
			}),
		onSuccess: ({ error: err }) => {
			if (err) setError(err.message ?? "تعذر تغيير الصلاحية");
			else invalidate();
		},
	});

	const toggleBanMutation = useMutation({
		mutationFn: (user: AdminUser) =>
			user.banned
				? authClient.admin.unbanUser({ userId: user.id })
				: authClient.admin.banUser({ userId: user.id }),
		onSuccess: ({ error: err }) => {
			if (err) setError(err.message ?? "تعذر تحديث حالة الحظر");
			else invalidate();
		},
	});

	const users = usersQuery.data ?? [];

	return (
		<div className="flex flex-col gap-5">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-navy">المستخدمون</h1>
					<p className="text-sm text-muted">
						إدارة حسابات فريق الإدارة والتجاريين.
					</p>
				</div>
				<PrimaryButton onClick={() => setShowCreate(true)}>
					<UserPlus className="h-4 w-4" />
					مستخدم جديد
				</PrimaryButton>
			</header>

			{error ? (
				<p className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">
					{error}
				</p>
			) : null}

			<div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
				<table className="w-full min-w-[640px] text-start text-sm">
					<thead>
						<tr className="border-b border-line bg-canvas text-muted">
							<th className="px-4 py-3 text-start font-bold">الاسم</th>
							<th className="px-4 py-3 text-start font-bold">
								البريد الإلكتروني
							</th>
							<th className="px-4 py-3 text-start font-bold">الصلاحية</th>
							<th className="px-4 py-3 text-start font-bold">الحالة</th>
							<th className="px-4 py-3 text-start font-bold">إجراءات</th>
						</tr>
					</thead>
					<tbody>
						{users.map((user) => (
							<tr
								key={user.id}
								className="border-b border-line transition-colors last:border-0 hover:bg-canvas/60"
							>
								<td className="px-4 py-3 font-semibold text-ink">
									{user.name}
								</td>
								<td className="px-4 py-3 text-ink" dir="ltr">
									{user.email}
								</td>
								<td className="px-4 py-3">
									<select
										value={user.role ?? "commercial"}
										disabled={setRoleMutation.isPending}
										onChange={(e) =>
											setRoleMutation.mutate({
												userId: user.id,
												role: e.target.value,
											})
										}
										className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs font-semibold text-navy outline-none focus:border-teal"
									>
										{ROLES.map((role) => (
											<option key={role} value={role}>
												{ROLE_LABELS[role]}
											</option>
										))}
									</select>
								</td>
								<td className="px-4 py-3">
									{user.banned ? (
										<span className="rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
											محظور
										</span>
									) : (
										<span className="rounded-md bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-600">
											نشِط
										</span>
									)}
								</td>
								<td className="px-4 py-3">
									<button
										type="button"
										disabled={toggleBanMutation.isPending}
										onClick={() => toggleBanMutation.mutate(user)}
										className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs font-bold text-navy transition-colors hover:bg-canvas disabled:opacity-50"
									>
										{user.banned ? (
											<ShieldCheck className="h-3.5 w-3.5" />
										) : (
											<Ban className="h-3.5 w-3.5" />
										)}
										{user.banned ? "إلغاء الحظر" : "حظر"}
									</button>
								</td>
							</tr>
						))}

						{usersQuery.isLoading ? (
							<tr>
								<td colSpan={5} className="px-4 py-16 text-center">
									<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted" />
								</td>
							</tr>
						) : null}

						{!usersQuery.isLoading && users.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-4 py-16 text-center text-muted">
									لا يوجد مستخدمون بعد.
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>

			{showCreate ? (
				<CreateUserDialog
					onClose={() => setShowCreate(false)}
					onCreated={() => {
						setShowCreate(false);
						invalidate();
					}}
				/>
			) : null}
		</div>
	);
}

function CreateUserDialog({
	onClose,
	onCreated,
}: {
	onClose: () => void;
	onCreated: () => void;
}) {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [role, setRole] = useState<Role>("commercial");
	const [error, setError] = useState<string | null>(null);

	const createMutation = useMutation({
		// `as never`: see setRoleMutation above — same client role-type mismatch.
		mutationFn: () =>
			authClient.admin.createUser({
				name,
				email,
				password,
				role: role as never,
			}),
		onSuccess: ({ error: err }) => {
			if (err) setError(err.message ?? "تعذر إنشاء المستخدم");
			else onCreated();
		},
	});

	return (
		<Modal
			title="مستخدم جديد"
			onClose={onClose}
			footer={
				<div className="flex justify-end gap-2">
					<GhostButton onClick={onClose}>إلغاء</GhostButton>
					<PrimaryButton
						disabled={
							createMutation.isPending || !name || !email || password.length < 8
						}
						onClick={() => createMutation.mutate()}
					>
						{createMutation.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : null}
						إنشاء
					</PrimaryButton>
				</div>
			}
		>
			<div className="flex flex-col gap-4">
				<Field label="الاسم الكامل">
					<input
						value={name}
						onChange={(e) => setName(e.target.value)}
						className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-teal"
					/>
				</Field>
				<Field label="البريد الإلكتروني">
					<input
						type="email"
						dir="ltr"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-teal"
					/>
				</Field>
				<Field label="كلمة المرور (8 أحرف على الأقل)">
					<input
						type="password"
						dir="ltr"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-teal"
					/>
				</Field>
				<Field label="الصلاحية">
					<select
						value={role}
						onChange={(e) => setRole(e.target.value as Role)}
						className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-teal"
					>
						{ROLES.map((r) => (
							<option key={r} value={r}>
								{ROLE_LABELS[r]}
							</option>
						))}
					</select>
					{/* The roles differ in reach, not seniority — spell it out so a
					    community manager isn't created by accident. */}
					<p className="mt-1.5 text-xs text-muted">{ROLE_HINTS[role]}</p>
				</Field>

				{error ? (
					<p className="text-sm font-semibold text-red-600">{error}</p>
				) : null}
			</div>
		</Modal>
	);
}

function Field({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) {
	return (
		// biome-ignore lint/a11y/noLabelWithoutControl: the input is `children`, implicitly associated
		<label className="flex flex-col gap-1.5 text-sm font-bold text-navy">
			{label}
			{children}
		</label>
	);
}
